import { useEffect } from 'react';
import { FieldValues, Path } from 'react-hook-form/dist/types';
import { useSearchParams } from 'react-router';

import { ControlledTextFieldProps } from './controlled-textfield.model';
import { ControlledTextFieldView } from './controlled-textfield.view';

// Decision making component
// This component will be responsible for deciding which UI to render
// based on the current state of the application
export const ControlledTextFieldController = <T extends FieldValues>(
  props: ControlledTextFieldProps<T>
) => {
  const [searchParams] = useSearchParams();
  const { attribute, setValue, watch } = props;

  // Auto-populate dynamic relationship fields from URL params
  useEffect(() => {
    if (!setValue) return;

    const parentRecordName =
      searchParams.get('pname') ?? searchParams.get('pr');
    const parentRecordLabel = searchParams.get('plabel');
    const parentObjectName = searchParams.get('po');

    // Check field type using both key and columnName for compatibility
    const isObjectRecordName =
      attribute?.key === 'objectRecordName' ||
      attribute?.columnName === 'objectRecordName';
    const isObjectRecordLabel =
      attribute?.key === 'objectRecordLabel' ||
      attribute?.columnName === 'objectRecordLabel';

    // Only proceed if this is a dynamic relationship field
    if (!isObjectRecordName && !isObjectRecordLabel) return;

    // Check if we have parent info and field hasn't been manually changed
    const currentValue = watch ? watch(attribute.name as Path<T>) : undefined;
    if (currentValue) return; // Don't overwrite if user has entered value

    // Use setTimeout to ensure form is mounted (important for modals)
    // Increased to 500ms to ensure modal is fully rendered
    setTimeout(() => {
      // Auto-populate objectRecordName field
      if (parentObjectName && (isObjectRecordName || isObjectRecordLabel)) {
        if (parentRecordName) {
          setValue(
            'object_record_name__s' as Path<T>,
            parentRecordName as any,
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            }
          );
        }
        if (parentRecordLabel) {
          setValue(
            'object_record_label__s' as Path<T>,
            parentRecordLabel as any,
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            }
          );
        }
      }
    }, 100);
  }, [
    attribute?.columnName,
    attribute?.key,
    attribute.name,
    searchParams,
    setValue,
    watch,
  ]);

  return <ControlledTextFieldView {...props} />;
};
